import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GlobalOrdersRequest } from '@api/ringil-api.v2.tsx';
import ActionMenu, { ActionMenuItem } from '@components/RingilComponents/ActionMenu.tsx';
import NavigationBreadCrumb from '@components/ringil3/BreadCrumb/NavigationBreadCrumb.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgArrowUp from '@icons/arrow-up.svg?react';
import SvgPlus from '@icons/plus-thick.svg?react';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import SvgShuffle from '@icons/shuffle.svg?react';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { OrdersConfig } from '@components/framework/orders/validation.ts';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Form } from '@hooks/useForm/useForm.tsx';
import { ExpeditionLocality, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { getRouteTestIds } from '@app/pages/expedition/create/testIds.ts';
import { RouteOrderIssue } from '@app/pages/expedition/create/routeRules.ts';
import RouteLocationCard from '@app/pages/expedition/create/components/RouteLocationCard.tsx';
import LocationOrdersPanel from '@app/pages/expedition/create/components/LocationOrdersPanel.tsx';
import MergeStopDialog from '@app/pages/expedition/create/components/MergeStopDialog.tsx';
import { sameLocationCandidates } from '@app/pages/expedition/create/stopMerge.ts';

interface ComponentProps {
  form: Form<NewExpeditionForm>;
  location: ExpeditionLocality;
  locIndex: number;
  locationCount: number;
  ordersConfig: OrdersConfig;
  companyId: string;
  canDeleteLocation: boolean;
  onDeleteLocation: (locIndex: number) => void;
  /** Folds this stop into another at the same place; absent outside a merge, which hides the action. */
  onMergeStop?: (sourceIndex: number, targetIndex: number) => void;
  onMoveStop: (fromIndex: number, toIndex: number) => void;
  onAddStopAbove: (index: number) => void;
  onDeleteUnloading: (locIndex: number) => void;
  unloadableOrders: GlobalOrdersRequest[];
  onUnloadOrder: (locIndex: number, orderId: string) => void;
  onOrderAdded: (locIndex: number, orderId: string) => void;
  onMoveOrderLoading: (orderId: string, targetLocIndex: number) => void;
  orderIssues: RouteOrderIssue[];
  animateEntry: boolean;
  /** This stop is wanted earlier than the one before it; a hint, nothing is blocked. */
  startsBeforePrevious?: boolean;
}

/**
 * One stop of the route: its position controls, the location details and the cargo alongside them.
 *
 * Kept as its own component so per-stop state stays out of the page — anything held higher up
 * re-renders every stop, reloading the location pickers and timeslot editors along with it.
 */
const RouteStop: React.FC<ComponentProps> = ({
                                               form,
                                               location: loc,
                                               locIndex,
                                               locationCount,
                                               ordersConfig,
                                               companyId,
                                               canDeleteLocation,
                                               onDeleteLocation,
                                               onMergeStop,
                                               onMoveStop,
                                               onAddStopAbove,
                                               onDeleteUnloading,
                                               unloadableOrders,
                                               onUnloadOrder,
                                               onOrderAdded,
                                               onMoveOrderLoading,
                                               orderIssues,
                                               animateEntry,
                                               startsBeforePrevious = false,
                                             }) => {
  const { tr } = useTranslationLgs();

  const routeTestIds = getRouteTestIds(locIndex, locationCount);
  const isLastLocation = locIndex === locationCount - 1;
  const [mergeDialogVisible, setMergeDialogVisible] = useState(false);

  // Same place on another day is what the automatic merge leaves apart; folding it is the user's call.
  // A route down to its two mandatory stops cannot lose one, so nothing is offered there.
  const mergeCandidates = onMergeStop && canDeleteLocation ? sameLocationCandidates(form.values.locations, locIndex) : [];
  const overlappingCandidates = mergeCandidates.filter(candidate => candidate.overlaps);

  const mergeInto = (targetIndex: number) => {
    setMergeDialogVisible(false);
    onMergeStop(locIndex, targetIndex);
  };

  // Everything that can be done to the stop as a whole, gathered behind one control instead of a
  // row of icon buttons. Moves that would run off either end are shown disabled rather than hidden,
  // so the menu keeps the same shape on every stop.
  const stopActions: ActionMenuItem[] = [
    {
      key: 'moveUp',
      label: tr('CreateExpedition.moveStopUp', 'Move up'),
      icon: <SvgArrowUp />,
      disabled: locIndex === 0,
      onClick: () => onMoveStop(locIndex, locIndex - 1),
      dataTestId: `${routeTestIds.locationSelect}-moveUp`,
    },
    {
      key: 'moveDown',
      // `arrow-down.svg` hardcodes a grey fill, so the up arrow is reused rotated.
      label: tr('CreateExpedition.moveStopDown', 'Move down'),
      icon: <SvgArrowUp className={'rotate-180'} />,
      disabled: isLastLocation,
      onClick: () => onMoveStop(locIndex, locIndex + 1),
      dataTestId: `${routeTestIds.locationSelect}-moveDown`,
    },
    {
      key: 'addAbove',
      label: tr('CreateExpedition.addStopAbove', 'Add stop above'),
      icon: <SvgPlus />,
      onClick: () => onAddStopAbove(locIndex),
      dataTestId: `${routeTestIds.locationSelect}-addAbove`,
    },
    ...(onMergeStop
      ? [
          {
            key: 'mergeSameLocation',
            label: tr('CreateExpedition.mergeStopAction', 'Merge with the same location on another day…'),
            icon: <SvgShuffle />,
            disabled: mergeCandidates.length === 0,
            onClick: () => setMergeDialogVisible(true),
            dataTestId: `${routeTestIds.locationSelect}-mergeSameLocation`,
          },
        ]
      : []),
    {
      key: 'delete',
      label: tr('CreateExpedition.deleteStop', 'Delete stop'),
      icon: <SvgDelete />,
      danger: true,
      disabled: !canDeleteLocation,
      onClick: () => onDeleteLocation(locIndex),
      dataTestId: `${routeTestIds.locationSelect}-delete`,
    },
  ];

  return (
    <motion.div
      className={'flex flex-col gap-4'}
      initial={animateEntry ? { opacity: 0, y: 10 } : false}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className={'flex flex-row items-center justify-between mr'}>
        <NavigationBreadCrumb
          locationCount={locationCount}
          currentPosition={locIndex}
          idPrefix={'createExp'}
          locations={form.values?.locations.map((location, idx) => ({
            id: location.location?.locationId ?? String(idx),
            name: location.location?.locationName ?? location.manualAddress?.name ?? '',
          }))}
        />
        <ActionMenu
          icon={<SvgPencil />}
          tooltip={tr('CreateExpedition.stopActionsTooltip', 'Stop actions')}
          heading={tr('CreateExpedition.stopActionsHeading', 'Stop actions')}
          items={stopActions}
          dataTestId={`${routeTestIds.locationSelect}-actions`}
        />
      </div>

      {startsBeforePrevious && (
        <COInlineMessage variant={'warning'} dataTestId={`${routeTestIds.locationSelect}-startsBeforePrevious`}>
          {tr(
            'CreateExpedition.stopStartsBeforePrevious',
            'This stop is wanted earlier than the stop before it. The optimized order only follows the loading dates — check the times or reorder the stops.',
          )}
        </COInlineMessage>
      )}

      {/* The date now matches another stop at the same place — offer to fold this one into it. */}
      {overlappingCandidates.length > 0 && (
        <COInlineMessage
          variant={'info'}
          dataTestId={`${routeTestIds.locationSelect}-sameLocationOverlap`}
          buttons={
            <div className={'flex flex-row flex-wrap gap-2'}>
              {overlappingCandidates.map(candidate => (
                <RingilButton
                  key={candidate.index}
                  mode={'outlined'}
                  size={'small'}
                  label={tr('CreateExpedition.mergeStopInto', 'Merge into stop {{stop}}', { stop: candidate.index + 1 })}
                  onClick={() => mergeInto(candidate.index)}
                  dataTestId={`${routeTestIds.locationSelect}-mergeInto-${candidate.index}`}
                />
              ))}
            </div>
          }
        >
          {tr(
            'CreateExpedition.mergeStopOverlap',
            'Another stop at the same location is planned for the same time. Merge this stop into it to move its orders there.',
          )}
        </COInlineMessage>
      )}

      <div className={'grid grid-cols-1 gap-4 lg:grid-cols-2'}>
        <RouteLocationCard form={form} locIndex={locIndex} location={loc} testIds={routeTestIds} />

        <LocationOrdersPanel
          form={form}
          locIndex={locIndex}
          location={loc}
          locationCount={locationCount}
          ordersConfig={ordersConfig}
          companyId={companyId}
          testIds={routeTestIds}
          onDeleteUnloading={onDeleteUnloading}
          unloadableOrders={unloadableOrders}
          onUnloadOrder={onUnloadOrder}
          onOrderAdded={onOrderAdded}
          onMoveOrderLoading={onMoveOrderLoading}
          orderIssues={orderIssues}
        />
      </div>

      {onMergeStop && (
        <MergeStopDialog
          visible={mergeDialogVisible}
          locIndex={locIndex}
          candidates={mergeCandidates}
          onHide={() => setMergeDialogVisible(false)}
          onMerge={mergeInto}
        />
      )}
    </motion.div>
  );
};

export default RouteStop;